// src/components/accueil/personnaliser/ComposerFile.tsx — SCE Issues
// Composer une file en bulles (lot 5 du chantier du 03/11, maquette
// https://claude.ai/artifact/W2ntCLABGPXsYQmGhaZeBZ) : un nom, puis métier,
// statut, criticité, « ne garder que », sans activité depuis, l'ordre. Le nombre
// et les premiers problèmes se mettent à jour à chaque bulle.
//
// Les choix sont rangés comme les filtres d'une vue du tableau de /issues : même
// forme, mêmes règles (utils/filesComposees). OU dans une rangée, ET entre rangées.

import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { libelleCle, type ClePerimetre } from '@/config/sujets.config'
import { useClicFond } from '@/hooks/useClicFond'
import { DELAIS_SANS_ACTIVITE } from '@/utils/dispositionAccueil'
import { erreurNomFile, LONGUEUR_MAX_NOM_FILE, ORDRES_FILE, type FileComposee } from '@/utils/filesComposees'
import type { Issue } from '@/types/issue.types'

const STATUTS: readonly { valeur: string; libelle: string }[] = [
  { valeur: 'Ouvert', libelle: 'À qualifier' },
  { valeur: 'En cours', libelle: 'En cours' },
  { valeur: 'Clos', libelle: 'Clos' },
]
const CRITICITES = ['Critique', 'Majeur', 'Mineur'] as const

export function ComposerFile({
  initiale,
  metiers,
  nomsPris,
  calculer,
  onValider,
  onAnnuler,
}: {
  initiale: FileComposee
  /** Les métiers (et sujets) proposés en bulles. */
  metiers: readonly ClePerimetre[]
  /** Noms des autres files (un nom ne se répète pas). */
  nomsPris: readonly string[]
  calculer: (f: FileComposee) => Issue[]
  onValider: (f: FileComposee) => void
  onAnnuler: () => void
}): JSX.Element {
  const [f, setF] = useState<FileComposee>(initiale)
  const [nomTouche, setNomTouche] = useState(false)
  const fond = useClicFond(onAnnuler)
  const champNom = useRef<HTMLInputElement>(null)
  const apercu = useMemo(() => calculer(f), [calculer, f])
  const erreur = erreurNomFile(f.nom, nomsPris)
  const nouvelle = !initiale.nom

  useEffect(() => {
    champNom.current?.focus()
    function surEchap(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      onAnnuler()
    }
    window.addEventListener('keydown', surEchap, true)
    return () => window.removeEventListener('keydown', surEchap, true)
  }, [onAnnuler])

  const valeurs = (col: 'metier' | 'statut' | 'criticite'): string[] => f.filtres[col] ?? []
  const basculer = (col: 'metier' | 'statut' | 'criticite', v: string): void => {
    const actuelles = valeurs(col)
    const suivantes = actuelles.includes(v) ? actuelles.filter((x) => x !== v) : [...actuelles, v]
    const filtres = { ...f.filtres }
    if (suivantes.length) filtres[col] = suivantes
    else delete filtres[col]
    setF({ ...f, filtres })
  }

  return createPortal(
    <div className="confirmation-voile" {...fond} role="presentation">
      <div className="perso-composer" role="dialog" aria-modal="true" aria-labelledby="composer-titre">
        <h2 id="composer-titre" className="confirmation-titre">{nouvelle ? 'Composer une file' : `Modifier « ${initiale.nom} »`}</h2>

        <label className="perso-composer-nom">
          <span>Nom de la file</span>
          <input
            ref={champNom}
            value={f.nom}
            maxLength={LONGUEUR_MAX_NOM_FILE}
            placeholder="Ex. Critiques Élec"
            onChange={(e) => setF({ ...f, nom: e.target.value })}
            onBlur={() => setNomTouche(true)}
            aria-invalid={nomTouche && !!erreur}
          />
          {nomTouche && erreur && <span className="perso-composer-erreur">{erreur}</span>}
        </label>

        <Rangee titre="Métier" aide="aucun = tous">
          {metiers.map((m) => (
            <Bulle key={m} actif={valeurs('metier').includes(m)} onClick={() => basculer('metier', m)}>
              {libelleCle(m)}
            </Bulle>
          ))}
        </Rangee>
        <Rangee titre="Statut" aide="aucun = tous, sauf annulés">
          {STATUTS.map((s) => (
            <Bulle key={s.valeur} actif={valeurs('statut').includes(s.valeur)} onClick={() => basculer('statut', s.valeur)}>
              {s.libelle}
            </Bulle>
          ))}
        </Rangee>
        <Rangee titre="Criticité" aide="aucune = toutes">
          {CRITICITES.map((c) => (
            <Bulle key={c} actif={valeurs('criticite').includes(c)} onClick={() => basculer('criticite', c)}>
              {c}
            </Bulle>
          ))}
        </Rangee>
        <Rangee titre="Ne garder que">
          <Bulle actif={f.mesDeclarations} onClick={() => setF({ ...f, mesDeclarations: !f.mesDeclarations })}>Mes déclarations</Bulle>
          <Bulle actif={f.suivis} onClick={() => setF({ ...f, suivis: !f.suivis })}>Ce que je suis</Bulle>
          <Bulle actif={f.enFabrication} onClick={() => setF({ ...f, enFabrication: !f.enFabrication })}>En fabrication</Bulle>
        </Rangee>
        <Rangee titre="Sans activité depuis">
          <Bulle actif={f.sansActiviteDepuis === null} onClick={() => setF({ ...f, sansActiviteDepuis: null })}>Peu importe</Bulle>
          {DELAIS_SANS_ACTIVITE.map((d) => (
            <Bulle key={d} actif={f.sansActiviteDepuis === d} onClick={() => setF({ ...f, sansActiviteDepuis: d })}>
              {d} jours
            </Bulle>
          ))}
        </Rangee>
        <Rangee titre="Ordre">
          {ORDRES_FILE.map((o) => (
            <Bulle key={o.ordre} actif={f.ordre === o.ordre} onClick={() => setF({ ...f, ordre: o.ordre })}>
              {o.libelle}
            </Bulle>
          ))}
        </Rangee>

        <div className="perso-composer-apercu" aria-live="polite">
          <strong>
            {apercu.length} problème{apercu.length > 1 ? 's' : ''} aujourd’hui
          </strong>
          {apercu.slice(0, 3).map((i) => (
            <span key={i.id}>
              {i.id} · {i.titre}
            </span>
          ))}
          {apercu.length > 3 && <span>… et {apercu.length - 3} autre{apercu.length - 3 > 1 ? 's' : ''}</span>}
        </div>

        <div className="confirmation-boutons">
          <button type="button" className="confirmation-annuler" onClick={onAnnuler}>
            Annuler
          </button>
          <button
            type="button"
            className="confirmation-confirmer"
            disabled={!!erreur}
            onClick={() => onValider({ ...f, nom: f.nom.trim() })}
          >
            {nouvelle ? 'Ajouter la file' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function Rangee({ titre, aide, children }: { titre: string; aide?: string; children: ReactNode }): JSX.Element {
  return (
    <fieldset className="perso-composer-rangee">
      <legend>
        {titre}
        {aide && <span> · {aide}</span>}
      </legend>
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {children}
      </div>
    </fieldset>
  )
}

function Bulle({ actif, onClick, children }: { actif: boolean; onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button type="button" className="perso-bulle" aria-pressed={actif} onClick={onClick}>
      {children}
    </button>
  )
}
